
export interface MultiLingualContent {
  text: string;
  language: 'en' | 'fr';
}

export interface License {
  id: string;
  name: string;
  description: string;
  rights_holder: string;
  mentions: string[];
  hashtags: string[];
  permissions: MultiLingualContent[];
  restrictions: MultiLingualContent[];
  createdAt: string;
  updatedAt: string;
}

export interface LicenseVariables {
  rightsholder: string;
  mentions: string[];
  hashtags: string[];
}

/**
 * Maps a database license object to our License type
 */
export function mapDatabaseLicenseToLicense(dbLicense: any): License {
  // Parse permissions from database format
  let permissions: MultiLingualContent[] = [];
  if (dbLicense.permissions) {
    try {
      // If it's a string array with our format "text|language"
      if (Array.isArray(dbLicense.permissions) && dbLicense.permissions.length > 0) {
        if (typeof dbLicense.permissions[0] === 'string') {
          if (dbLicense.permissions[0].includes('|')) {
            // Format "text|language"
            permissions = dbLicense.permissions.map((item: string) => {
              const [text, language] = item.split('|');
              return { text, language: language as 'en' | 'fr' };
            });
          } else {
            // Legacy format: convert simple strings to MultiLingualContent
            permissions = dbLicense.permissions.map((text: string) => ({ 
              text, 
              language: 'en' 
            }));
          }
        }
        // If already in MultiLingualContent format
        else if (typeof dbLicense.permissions[0] === 'object') {
          permissions = dbLicense.permissions;
        }
      }
      // If it's a JSON string, parse it
      else if (typeof dbLicense.permissions === 'string' && dbLicense.permissions.startsWith('[{')) {
        permissions = JSON.parse(dbLicense.permissions);
      }
    } catch (e) {
      console.error("Error parsing permissions:", e);
      // If parsing fails, leave as empty array
    }
  }

  // Parse restrictions from database format
  let restrictions: MultiLingualContent[] = [];
  if (dbLicense.restrictions) {
    try {
      // If it's a string array with our format "text|language"
      if (Array.isArray(dbLicense.restrictions) && dbLicense.restrictions.length > 0) {
        if (typeof dbLicense.restrictions[0] === 'string') {
          if (dbLicense.restrictions[0].includes('|')) {
            // Format "text|language"
            restrictions = dbLicense.restrictions.map((item: string) => {
              const [text, language] = item.split('|');
              return { text, language: language as 'en' | 'fr' };
            });
          } else {
            // Legacy format: convert simple strings to MultiLingualContent
            restrictions = dbLicense.restrictions.map((text: string) => ({ 
              text, 
              language: 'en' 
            }));
          }
        }
        // If already in MultiLingualContent format
        else if (typeof dbLicense.restrictions[0] === 'object') {
          restrictions = dbLicense.restrictions;
        }
      }
      // If it's a JSON string, parse it
      else if (typeof dbLicense.restrictions === 'string' && dbLicense.restrictions.startsWith('[{')) {
        restrictions = JSON.parse(dbLicense.restrictions);
      }
    } catch (e) {
      console.error("Error parsing restrictions:", e);
      // If parsing fails, leave as empty array
    }
  }

  return {
    id: dbLicense.id || "",
    name: dbLicense.name || "",
    description: dbLicense.description || "",
    rights_holder: dbLicense.rights_holder || "",
    mentions: dbLicense.mentions || [],
    hashtags: dbLicense.hashtags || [],
    permissions,
    restrictions,
    createdAt: dbLicense.created_at || new Date().toISOString(),
    updatedAt: dbLicense.updated_at || new Date().toISOString()
  };
}

/**
 * Gets a license by ID from the list of available licenses
 */
export function getLicenseById(licenseId: string, licenses: License[]): License | null {
  return licenses.find(license => license.id === licenseId) || null;
}

/**
 * Creates default license variables based on a license template
 */
export function createDefaultLicenseVariables(license: License): LicenseVariables {
  return {
    rightsholder: license.rights_holder || "",
    mentions: license.mentions || [],
    hashtags: license.hashtags || [],
  };
}

/**
 * Transform license variables for storage
 */
export function transformLicenseVariablesForStorage(variables: LicenseVariables) {
  return {
    rights_holder: variables.rightsholder,
    mentions: variables.mentions,
    hashtags: variables.hashtags
  };
}

/**
 * Gets content in the specified language, or the first available language as fallback
 */
export function getLocalizedContent(items: MultiLingualContent[], language: string): string[] {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return [];
  }
  
  // Find all items matching the requested language
  const localizedItems = items.filter(item => item.language === language);
  
  // If found, return those items
  if (localizedItems.length > 0) {
    return localizedItems.map(item => item.text);
  }
  
  // Otherwise, get all items from the first language available as fallback
  const firstLanguage = items[0].language;
  return items
    .filter(item => item.language === firstLanguage)
    .map(item => item.text);
}
